import ServicePrice from "../../components/ServicePrice";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Phone, MessageCircle, MapPin, Calendar, User, Save } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../AdminLayout";
import { adminAxios, formatErr } from "../api";
import { StatusPill } from "./AdminDashboard";
import { useCatalog } from "../../contexts/CatalogContext";

const STATUS_OPTIONS = ["confirmed", "assigned", "on_the_way", "completed", "cancelled"];

export default function AdminBookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [techs, setTechs] = useState([]);
  const [techId, setTechId] = useState("");
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [serviceDate, setServiceDate] = useState("");
  const [serviceSlot, setServiceSlot] = useState("");
  const [saving, setSaving] = useState(false);
  const [roPhotoOpen, setRoPhotoOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      adminAxios.get(`/bookings/${id}`),
      adminAxios.get("/technicians?status=active"),
    ]).then(([b, t]) => {
      setBooking(b.data);
      setTechs(t.data.items || []);
      setTechId(b.data.technician_id || "");
      setStatus(b.data.status || "");
      setNotes(b.data.internal_notes || "");
      setServiceDate(b.data.date || "");
      setServiceSlot(b.data.slot || "");
    }).catch((e) => toast.error(formatErr(e)));
  }, [id]);

  const { getCategoryById, getServiceById, CITIES, SLOTS } = useCatalog();
  const originalAmount = Number(booking?.price ?? 0);
  const extraAmount = Number(booking?.extra_amount ?? 0);
  const finalAmount = Number(booking?.final_price ?? booking?.price ?? 0);
  const hasExtraAmount = extraAmount > 0 || finalAmount > originalAmount;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { status, internal_notes: notes };
      if (serviceDate !== booking.date) payload.date = serviceDate;
      if (serviceSlot !== booking.slot) payload.slot = serviceSlot;
      if (techId !== (booking.technician_id || "")) {
        payload.technician_id = techId;
        const t = techs.find((x) => x.id === techId);
        payload.technician_name = t?.name || "";
      }
      const { data } = await adminAxios.patch(`/bookings/${id}`, payload);
      setBooking(data);
      toast.success("Booking updated");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (!booking) return <AdminLayout title="Booking"><div className="text-slate-500">Loading…</div></AdminLayout>;

  const cat = getCategoryById(booking.category);
  const svc = getServiceById(booking.category, booking.service);
  const slot = SLOTS.find((s) => s.id === booking.slot)?.label || booking.slot;
  const city = CITIES.find((c) => c.id === booking.city)?.name || booking.city;
  const mapsUrl = `https://www.google.com/maps?q=${encodeURIComponent(`${booking.address}, ${city}`)}`;
  const roPhotoUrl = booking?.ro_photo_url || booking?.product_image_url;

  return (
    <AdminLayout title={`Booking ${booking.booking_id}`}>
      {roPhotoOpen && roPhotoUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4"
          onClick={() => setRoPhotoOpen(false)}
        >
          <div className="relative max-w-5xl w-full flex items-center justify-center">
            <img
              src={roPhotoUrl}
              alt="RO inspection zoom"
              className="max-h-[90vh] max-w-full rounded-xl border border-white/20 shadow-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      <button onClick={() => navigate(-1)} className="mb-4 text-sm font-semibold text-slate-500 hover:text-[#1A3C6E] flex items-center gap-1" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left col */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Booking</div>
                <h2 className="font-display font-extrabold text-2xl text-slate-900 mt-1">{booking.booking_id}</h2>
                <div className="text-sm text-slate-500 mt-1">{new Date(booking.created_at).toLocaleString("en-IN")}</div>
              </div>
              <StatusPill status={booking.status} />
            </div>
          </Card>

          <Card title="Customer">
            <Row label="Name" value={booking.name} icon={User} />
            <Row label="Phone" value={booking.phone} icon={Phone} action={
              <div className="flex gap-2">
                <a href={`tel:${booking.phone}`} className="text-xs font-semibold text-[#1A3C6E] hover:underline" data-testid="detail-call">Call</a>
                <a href={`https://wa.me/91${booking.phone}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#22C55E] hover:underline" data-testid="detail-whatsapp">WhatsApp</a>
              </div>
            } />
            {booking.email && <Row label="Email" value={booking.email} />}
            <Row label="Segment" value={booking.customer_type} capitalize />
          </Card>

          <Card title="Service">
            <Row label="Category" value={cat?.name || booking.category} />
            <Row label="Service" value={svc?.name || booking.service_name} />
            <Row label="Brand" value={booking.brand} />
            <Row label="Service charge" value={<ServicePrice service={booking} />} />
            {hasExtraAmount && <Row label="Extra amount" value={`₹${extraAmount || (finalAmount - originalAmount)}`} />}
            <Row label="Final amount" value={`₹${finalAmount}`} />
            {booking.ro_type && <Row label="RO type" value={booking.ro_type} />}
            {booking.service_needed && <Row label="Service needed" value={booking.service_needed} />}
            {(booking.parts_replaced?.length || booking.parts_to_change?.length) && (
              <Row label="Parts changed" value={(booking.parts_replaced || booking.parts_to_change || []).join(", ")} />
            )}
            {booking.inspection_note && <Row label="Technician note" value={booking.inspection_note} />}
            {roPhotoUrl && (
              <div className="pt-2">
                <div className="text-xs text-slate-500 mb-2">RO photo</div>
                <button
                  type="button"
                  onClick={() => setRoPhotoOpen(true)}
                  className="block w-full text-left rounded-lg border border-slate-200 overflow-hidden cursor-zoom-in"
                >
                  <img
                    src={roPhotoUrl}
                    alt="RO inspection"
                    className="w-full max-h-72 object-contain bg-slate-50"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </button>
              </div>
            )}
          </Card>

          <Card title="Schedule & Address">
            <Row label="Date" value={booking.date} icon={Calendar} />
            <Row label="Slot" value={slot} capitalize />
            <Row label="Address" value={`${booking.address}, ${city}${booking.pincode ? " - " + booking.pincode : ""}`} icon={MapPin} action={
              <a href={mapsUrl} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#1A3C6E] hover:underline" data-testid="detail-maps">Open Maps</a>
            } />
            {booking.landmark && <Row label="Landmark" value={booking.landmark} />}
            {booking.notes && <Row label="Customer notes" value={booking.notes} />}
          </Card>
        </div>

        {/* Right col — manage panel */}
        <div className="space-y-4">
          <Card title="Manage booking">
            <div>
              <label htmlFor="service-date" className="text-xs font-semibold text-slate-600">Service date</label>
              <input id="service-date" type="date" value={serviceDate} onChange={e => setServiceDate(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200" />
            </div>
            <div className="mt-3">
              <label htmlFor="service-slot" className="text-xs font-semibold text-slate-600">Service slot</label>
              <select id="service-slot" value={serviceSlot} onChange={e => setServiceSlot(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white">
                {["morning", "afternoon", "evening", "urgent"].map(value => <option key={value} value={value}>{value}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Status</label>
              <select
                data-testid="manage-status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white outline-none focus:border-[#1A3C6E]"
              >
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div className="mt-3">
              <label className="text-xs font-semibold text-slate-600">Assign technician</label>
              <select
                data-testid="manage-tech"
                value={techId}
                onChange={(e) => setTechId(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white outline-none focus:border-[#1A3C6E]"
              >
                <option value="">— Unassigned —</option>
                {techs.map((t) => <option key={t.id} value={t.id}>{t.name} • {t.phone}</option>)}
              </select>
              {techs.length === 0 && (
                <p className="text-xs text-slate-400 mt-1">No active technicians yet. Approve an application first.</p>
              )}
            </div>
            <div className="mt-3">
              <label className="text-xs font-semibold text-slate-600">Internal notes</label>
              <textarea
                data-testid="manage-notes"
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 outline-none focus:border-[#1A3C6E]"
                placeholder="Notes visible to admins only…"
              />
            </div>
            <button
              onClick={save}
              disabled={saving}
              data-testid="manage-save"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#1A3C6E] hover:bg-[#132D53] text-white font-semibold py-2.5 rounded-lg disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save changes"} <Save className="w-4 h-4" />
            </button>
          </Card>

          {booking.technician_name && (
            <Card title="Assigned technician">
              <div className="font-semibold text-slate-900">{booking.technician_name}</div>
              <div className="text-xs text-slate-500 mt-0.5">ID: {booking.technician_id}</div>
              <Link to={`/admin/technicians/${booking.technician_id}`} className="mt-3 inline-block text-xs font-semibold text-[#1A3C6E] hover:underline">View profile →</Link>
            </Card>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

const Card = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    {title && <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-500 mb-3">{title}</h3>}
    {children}
  </div>
);

const Row = ({ label, value, icon: Icon, action, capitalize }) => (
  <div className="flex items-start justify-between gap-3 py-1.5">
    <div className="flex items-start gap-2 min-w-0">
      {Icon && <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />}
      <div className="min-w-0">
        <div className="text-xs text-slate-500">{label}</div>
        <div className={`text-sm font-semibold text-slate-900 break-words ${capitalize ? "capitalize" : ""}`}>{value || "—"}</div>
      </div>
    </div>
    {action}
  </div>
);
